import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { configApi } from '../../api/configApi';

/**
 * Dev-only: the viewport size in the header bar, as the Problems page shows it (server config
 * ShowViewportSize), so review screenshots name their size (client, 2026-10-05).
 *
 * <p>Rendered from this page into the header's bar rather than added to Header's route list: the
 * data-editing pages never change a shared file. Same classes and spot as Header's own tag;
 * pointer-events none, so it can never cover a control.</p>
 */
export function ViewportSizeTag() {
  const [enabled, setEnabled] = useState(false);
  const [target, setTarget] = useState<Element | null>(null);
  const [size, setSize] = useState({ w: window.innerWidth, h: window.innerHeight });

  useEffect(() => {
    let cancelled = false;
    configApi.getClientConfig().then(cfg => {
      if (!cancelled && cfg.showViewportSize) setEnabled(true);
    }).catch(() => { /* default off */ });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!enabled) return;
    setTarget(document.querySelector('header .main-header-height'));
    const handleResize = () => setSize({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [enabled]);

  if (!enabled || !target) return null;
  return createPortal(
    <div
      aria-hidden="true"
      className="pointer-events-none absolute bottom-0 left-0 px-4 text-xs font-bold text-red-700 font-mono leading-tight"
      data-testid="viewport-size-tag"
    >
      {size.w} x {size.h}
    </div>,
    target,
  );
}
